/**
 * CreateMiniProjectModal (DEV-1273) — create a blank mini-project from
 * scratch. Until now the only way to get a mini-project was instantiating
 * a playbook; this is the from-scratch entry the backend (POST
 * /api/mini-projects → CreateFromScratch) always supported but no UI
 * exposed.
 *
 * Minimal by design (minis stay "as mini as possible" per DEV-903): a
 * title, the owning community, and optional start/end dates. The project
 * is created in DRAFT — the Lead fills goals/tasks/team on the project
 * page, then activates. Opaque overlay per the CLAUDE.md rule.
 *
 * Self-sufficient: derives the user's member communities from the global
 * communityMembershipStore (same source CommunityPicker reads), so the
 * parent only needs to pass the entity + relationship arrays it already
 * holds.
 */

import { useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { createMiniProject } from "../../api/miniProjects";
import { communityMembershipStore } from "../../Data/store";
import { normalizeGuid } from "../../utils/guid";
import { pathFromLeafGuid, pathDisplay } from "../../utils/entityPath";
import styles from "./CreateMiniProjectModal.module.css";

/** Same shortlist the Mini-Project Settings budget editor offers, for
 *  parity. Any other 3-letter code can be set later from Settings. */
const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "CNY",
  "INR", "MXN", "BRL", "ZAR", "SEK", "NOK", "DKK",
];

interface Props {
  currentUserGuid: string;
  /** Community entities from App.tsx apiCommunityEntities. */
  communities: Array<Record<string, any>>;
  /** Parent→child relationships from App.tsx apiCommunityRelationships. */
  relationships?: Array<Record<string, any>>;
  onClose: () => void;
  /** Called with the new mini-project's guid so the parent can navigate. */
  onCreated: (miniProjectGuid: string) => void;
}

export default function CreateMiniProjectModal({
  currentUserGuid,
  communities,
  relationships = [],
  onClose,
  onCreated,
}: Props) {
  const { t } = useAppTranslation("pages");

  // ── Member communities (the only ones the backend lets you create in) ──
  const options = useMemo(() => {
    const upperUser = currentUserGuid.toUpperCase();
    const memberGuids = new Set<string>();
    for (const m of communityMembershipStore) {
      if (
        String(m.user_guid || "").toUpperCase() === upperUser &&
        String(m.status || "").toLowerCase() === "active"
      ) {
        memberGuids.add(String(m.community_entity_guid || "").toUpperCase());
      }
    }

    const out: { guid: string; label: string }[] = [];
    for (const e of communities || []) {
      const guid = normalizeGuid(e?.entity_guid || e?.entityGuid);
      if (!guid) continue;
      if (!memberGuids.has(guid.toUpperCase())) continue;
      const isActive = e?.is_active ?? e?.isActive ?? true;
      if (!isActive) continue;
      // Org-family only. Mini-projects ("workshop items") are owned by
      // organizational communities (entity_type 'organization' or 'org_*'),
      // never geographic communities (geo_*). The delegated
      // community:create_projects ("creation privileges") path is enforced
      // backend-side; surfacing those communities here is a follow-up.
      const et = String(e?.entity_type ?? e?.entityType ?? "").toLowerCase();
      const isOrgFamily = et === "organization" || et.startsWith("org_");
      if (!isOrgFamily) continue;
      const leaf = String(e?.name ?? "").trim();
      if (!leaf) continue;
      const rootToLeaf = pathFromLeafGuid(guid, communities as any, relationships as any);
      const parts = (rootToLeaf ?? [])
        .map((p: any) => String(p?.name ?? "").trim())
        .filter(Boolean);
      // leaf→root, excluding the leaf itself, for a compact context suffix
      const ancestors = parts.slice(0, -1).reverse();
      const label = ancestors.length ? `${leaf} — ${pathDisplay(ancestors)}` : leaf;
      out.push({ guid, label });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [currentUserGuid, communities, relationships]);

  const [title, setTitle] = useState("");
  const [communityGuid, setCommunityGuid] = useState(
    options.length === 1 ? options[0].guid : "",
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [budgetCurrency, setBudgetCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = useMemo(
    () => title.trim().length > 0 && communityGuid.length > 0,
    [title, communityGuid],
  );

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const hasBudget = budgetAmount.trim().length > 0;
      const created = await createMiniProject({
        title: title.trim(),
        ownerCommunityGuid: communityGuid,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        plannedBudgetAmount: hasBudget ? Number(budgetAmount) : null,
        plannedBudgetCurrency: hasBudget ? budgetCurrency : null,
        visibility: "community",
      });
      onCreated(created.miniProjectGuid);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [title, communityGuid, startDate, endDate, budgetAmount, budgetCurrency, onCreated]);

  const dialog = (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t("createMini.title")}</h2>
          <button
            className={styles.x}
            onClick={onClose}
            aria-label={t("miniProject.completion.close")}
          >
            ✕
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.intro}>{t("createMini.intro")}</p>

          <label className={styles.fl}>{t("createMini.nameLabel")}</label>
          <input
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("createMini.namePlaceholder")}
            maxLength={200}
            autoFocus
          />

          <label className={styles.fl}>{t("createMini.communityLabel")}</label>
          {options.length === 0 ? (
            <div className={styles.hint}>{t("createMini.communityNone")}</div>
          ) : (
            <select
              className={styles.input}
              value={communityGuid}
              onChange={(e) => setCommunityGuid(e.target.value)}
            >
              <option value="">{t("createMini.communityPick")}</option>
              {options.map((c) => (
                <option key={c.guid} value={c.guid}>
                  {c.label}
                </option>
              ))}
            </select>
          )}

          <div className={styles.dates}>
            <div className={styles.dateCol}>
              <label className={styles.fl}>{t("createMini.startLabel")}</label>
              <input
                className={styles.input}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className={styles.dateCol}>
              <label className={styles.fl}>{t("createMini.endLabel")}</label>
              <input
                className={styles.input}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <label className={styles.fl}>{t("createMini.budgetLabel")}</label>
          <div className={styles.budgetRow}>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder={t("createMini.budgetPlaceholder")}
            />
            <select
              className={styles.currency}
              value={budgetCurrency}
              onChange={(e) => setBudgetCurrency(e.target.value)}
              aria-label={t("createMini.currencyLabel")}
            >
              {COMMON_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className={styles.hint}>{t("createMini.budgetHint")}</div>

          <div className={styles.hint}>{t("createMini.draftHint")}</div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.foot}>
          <span className={styles.spacer} />
          <button className={styles.btnGhost} onClick={onClose} disabled={busy}>
            {t("miniProject.completion.cancel")}
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleCreate}
            disabled={busy || !valid}
          >
            {busy ? t("createMini.creating") : t("createMini.create")}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}
